import { ASSISTANT_PLAN_PROSE_CLASS } from "@/components/assistant/assistantPlanProse";
import { AssistantProposalActionBar } from "@/components/assistant/AssistantProposalActionBar";
import { AssistantMarkdownContent } from "@/components/chat/AssistantMarkdownContent";
import type { ApprovalBarProps } from "@/components/assistant/useAssistantWriteApprovalHandlers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import type { CustomRoleRecord } from "@/types";
import { useMemo, useState } from "react";
import type { CustomRoleProposalInput } from "./types";

type Props = {
  mode: "create" | "update";
  proposal: CustomRoleProposalInput;
  existing?: CustomRoleRecord | null;
  disabled?: boolean;
  showActions?: boolean;
  approvalBar?: ApprovalBarProps;
  embedded?: boolean;
  onApprove?: () => void;
  onEditSubmit?: (edited: CustomRoleProposalInput) => void;
};

function FieldRow({
  label,
  value,
  changed,
  editing,
  editValue,
  onEditValue,
  multiline,
}: {
  label: string;
  value: string;
  changed: boolean;
  editing: boolean;
  editValue: string;
  onEditValue: (v: string) => void;
  multiline?: boolean;
}) {
  return (
    <div className="grid gap-1.5 sm:grid-cols-[8.5rem_1fr] sm:items-start">
      <span className="text-xs font-medium uppercase tracking-wide text-[var(--lm-muted)] dark:text-slate-400">
        {label}
      </span>
      {editing ? (
        multiline ? (
          <Textarea
            value={editValue}
            onChange={(e) => onEditValue(e.target.value)}
            rows={4}
            className="min-h-[5rem] text-sm"
          />
        ) : (
          <Input value={editValue} onChange={(e) => onEditValue(e.target.value)} className="text-sm" />
        )
      ) : multiline && value.trim() ? (
        <div
          className={cn(
            changed && "rounded-md bg-amber-500/10 px-2 py-1 ring-1 ring-amber-500/30"
          )}
        >
          <AssistantMarkdownContent content={value} className={ASSISTANT_PLAN_PROSE_CLASS} />
        </div>
      ) : (
        <p
          className={cn(
            "break-words whitespace-pre-wrap text-[15px] leading-snug text-[var(--lm-foreground-strong)] dark:text-slate-100",
            changed && "rounded-md bg-amber-500/10 px-2 py-1 ring-1 ring-amber-500/30"
          )}
        >
          {value || "—"}
        </p>
      )}
    </div>
  );
}

export function CustomRoleProposalCard({
  mode,
  proposal,
  existing,
  disabled,
  showActions = true,
  approvalBar,
  embedded = false,
  onApprove,
  onEditSubmit,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(proposal.name ?? "");
  const [draftRoleKey, setDraftRoleKey] = useState(proposal.role_key ?? "");
  const [draftInstructions, setDraftInstructions] = useState(proposal.instructions ?? "");

  const title = mode === "create" ? "Create custom role" : "Update custom role";

  const changed = useMemo(
    () => ({
      name: existing ? (proposal.name ?? "") !== existing.name : false,
      role_key: existing ? (proposal.role_key ?? "") !== existing.role_key : false,
      instructions: existing
        ? (proposal.instructions ?? "") !== (existing.instructions ?? "")
        : false,
    }),
    [existing, proposal]
  );

  function startEdit() {
    setDraftName(proposal.name ?? "");
    setDraftRoleKey(proposal.role_key ?? "");
    setDraftInstructions(proposal.instructions ?? "");
    setEditing(true);
  }

  function submitEdit() {
    onEditSubmit?.({
      id: proposal.id,
      name: draftName.trim(),
      role_key: draftRoleKey.trim(),
      instructions: draftInstructions.trim(),
    });
    setEditing(false);
  }

  return (
    <div
      className={cn(
        "overflow-hidden bg-white dark:bg-white/10",
        embedded
          ? "border-0 shadow-none"
          : "rounded-xl border border-[rgba(15,23,42,0.09)] shadow-[var(--shadow-card)] dark:border-white/10 dark:shadow-none"
      )}
    >
      {embedded ? null : (
        <div className="border-b border-[var(--lm-border-subtle)] bg-[var(--surface-sunken)] px-4 py-2.5 dark:border-white/10 dark:bg-white/5">
          <p className="text-sm font-semibold text-[var(--lm-foreground-strong)]">{title}</p>
        </div>
      )}
      <div className="space-y-3 px-4 py-3">
        <FieldRow
          label="Name"
          value={proposal.name ?? ""}
          changed={changed.name}
          editing={showActions && editing}
          editValue={draftName}
          onEditValue={setDraftName}
        />
        <FieldRow
          label="Role key"
          value={proposal.role_key ?? ""}
          changed={changed.role_key}
          editing={showActions && editing}
          editValue={draftRoleKey}
          onEditValue={setDraftRoleKey}
        />
        <FieldRow
          label="Instructions"
          value={proposal.instructions ?? ""}
          changed={changed.instructions}
          editing={showActions && editing}
          editValue={draftInstructions}
          onEditValue={setDraftInstructions}
          multiline
        />
      </div>
      {!embedded && approvalBar ? (
        <AssistantProposalActionBar mode={mode} {...approvalBar} />
      ) : showActions ? (
        <div className="flex flex-wrap justify-end gap-2 border-t border-[var(--lm-border-subtle)] px-4 py-3 dark:border-white/10">
          {editing ? (
            <>
              <Button type="button" variant="surface" size="sm" onClick={() => setEditing(false)} disabled={disabled}>
                Cancel
              </Button>
              <Button type="button" variant="default" size="sm" onClick={submitEdit} disabled={disabled}>
                Apply edits
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="surface" size="sm" onClick={startEdit} disabled={disabled}>
                Edit
              </Button>
              <Button type="button" variant="default" size="sm" onClick={() => onApprove?.()} disabled={disabled}>
                {mode === "create" ? "Approve & Create" : "Approve & Update"}
              </Button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
